import React, { useState } from 'react';
import { Settings, X, Search, Sliders, Globe, Key, Mail } from 'lucide-react';

interface EmailSearchSettingsModalProps {
  show: boolean;
  onClose: () => void;
  onSave: (settings: EmailSearchSettings) => void;
  currentSettings: EmailSearchSettings;
}

export interface EmailSearchSettings {
  minEmails: number;
  maxEmails: number;
  includeBusinessEmails: boolean;
  includePersonalEmails: boolean;
  prioritizeDomains: string[];
  excludeDomains: string[];
  targetCountries: string[];
  maxEmailsPerDomain: number;
  apiKey?: string;
}

export default function EmailSearchSettingsModal({
  show,
  onClose,
  onSave,
  currentSettings
}: EmailSearchSettingsModalProps) {
  const [settings, setSettings] = useState<EmailSearchSettings>({
    ...currentSettings,
    maxEmailsPerDomain: Number(currentSettings.maxEmailsPerDomain) || 2,
    apiKey: currentSettings.apiKey || '',
    // Ensure Malta is in the target countries if empty
    targetCountries: currentSettings.targetCountries.length ? currentSettings.targetCountries : ['MT']
  });
  const [newDomain, setNewDomain] = useState("");
  const [newExcludeDomain, setNewExcludeDomain] = useState("");
  const [newCountry, setNewCountry] = useState("");
  const [showSecrets, setShowSecrets] = useState(false);

  const handleAddDomain = () => {
    if (newDomain && !settings.prioritizeDomains.includes(newDomain)) {
      setSettings({
        ...settings,
        prioritizeDomains: [...settings.prioritizeDomains, newDomain]
      });
      setNewDomain("");
    }
  };

  const handleRemoveDomain = (domain: string) => {
    setSettings({
      ...settings,
      prioritizeDomains: settings.prioritizeDomains.filter(d => d !== domain)
    });
  };

  const handleAddExcludeDomain = () => {
    if (newExcludeDomain && !settings.excludeDomains.includes(newExcludeDomain)) {
      setSettings({
        ...settings,
        excludeDomains: [...settings.excludeDomains, newExcludeDomain]
      });
      setNewExcludeDomain("");
    }
  };

  const handleRemoveExcludeDomain = (domain: string) => {
    setSettings({
      ...settings,
      excludeDomains: settings.excludeDomains.filter(d => d !== domain)
    });
  };

  const handleAddCountry = () => {
    if (newCountry && !settings.targetCountries.includes(newCountry)) {
      setSettings({
        ...settings,
        targetCountries: [...settings.targetCountries, newCountry.toUpperCase()]
      });
      setNewCountry("");
    }
  };

  const handleRemoveCountry = (country: string) => {
    setSettings({
      ...settings,
      targetCountries: settings.targetCountries.filter(c => c !== country)
    });
  };

  const handleSave = () => {
    onSave(settings);
    onClose();
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Settings className="w-6 h-6" />
              Email Search Settings
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-6">
            {/* Email Limits */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Email Limits
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Minimum Emails
                  </label>
                  <input
                    type="number"
                    value={settings.minEmails}
                    min="1"
                    max={settings.maxEmails}
                    onChange={(e) => setSettings({
                      ...settings,
                      minEmails: Math.max(1, parseInt(e.target.value) || 1)
                    })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Maximum Emails
                  </label>
                  <input
                    type="number"
                    value={settings.maxEmails}
                    min={settings.minEmails}
                    max="1000"
                    onChange={(e) => setSettings({
                      ...settings,
                      maxEmails: Math.max(settings.minEmails, parseInt(e.target.value) || settings.minEmails)
                    })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Max Emails per Domain
                  </label>
                  <input
                    type="number"
                    value={settings.maxEmailsPerDomain}
                    min="1"
                    max="10"
                    onChange={(e) => setSettings({
                      ...settings,
                      maxEmailsPerDomain: Math.max(1, parseInt(e.target.value) || 1)
                    })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Email Types */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Sliders className="w-5 h-5" />
                Email Types
              </h3>
              <div className="space-y-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={settings.includeBusinessEmails}
                    onChange={(e) => setSettings({
                      ...settings,
                      includeBusinessEmails: e.target.checked
                    })}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <span>Include Business Emails</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={settings.includePersonalEmails}
                    onChange={(e) => setSettings({
                      ...settings,
                      includePersonalEmails: e.target.checked
                    })}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <span>Include Personal Emails</span>
                </label>
              </div>
            </div>

            {/* Priority Domains */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Priority Domains</h3>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  placeholder="Enter domain (e.g., example.com)"
                  className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                <button
                  onClick={handleAddDomain}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {settings.prioritizeDomains.map((domain) => (
                  <span
                    key={domain}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full"
                  >
                    {domain}
                    <button
                      onClick={() => handleRemoveDomain(domain)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Excluded Domains */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Excluded Domains</h3>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newExcludeDomain}
                  onChange={(e) => setNewExcludeDomain(e.target.value)}
                  placeholder="Enter domain to exclude"
                  className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                <button
                  onClick={handleAddExcludeDomain}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {settings.excludeDomains.map((domain) => (
                  <span
                    key={domain}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-800 rounded-full"
                  >
                    {domain}
                    <button
                      onClick={() => handleRemoveExcludeDomain(domain)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Target Countries */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Globe className="w-5 h-5" />
                Target Countries
              </h3>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newCountry}
                  onChange={(e) => setNewCountry(e.target.value)}
                  placeholder="Enter country code (e.g., US)"
                  className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                <button
                  onClick={handleAddCountry}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {settings.targetCountries.map((country) => (
                  <span
                    key={country}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full"
                  >
                    {country}
                    <button
                      onClick={() => handleRemoveCountry(country)}
                      className="text-green-600 hover:text-green-800"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* API Key */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Key className="w-5 h-5" />
                OpenAI API Key
              </h3>
              <p className="text-sm text-gray-600">
                Enter your OpenAI API key to enable AI-powered email search and content enhancement.
                Get your key from the <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700">OpenAI dashboard</a>.
              </p>
              <div className="relative">
                <input
                  type={showSecrets ? "text" : "password"}
                  value={settings.apiKey}
                  pattern="^sk-[a-zA-Z0-9]+$"
                  onChange={(e) => setSettings({
                    ...settings,
                    apiKey: e.target.value
                  })}
                  placeholder="sk-..."
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setShowSecrets(!showSecrets)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showSecrets ? "Hide" : "Show"}
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-4 pt-4">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Save Settings
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}